/**
 * cartApi.ts
 *
 * Store API client and cart data extensions for TEK TIDES LABS.
 *
 * Provides typed access to full WooCommerce Store API cart payload
 * (including coupons, fees/discounts, shipping, tax, quantity limits,
 * variation labels and regular vs current prices) without performing
 * any client-side price or discount calculation.
 */

import {
  formatCartMoney,
  minorToMajor,
  normalizeCart,
  type CartCurrency,
  type CartItemView,
  type CartTotalsView,
  type CartView,
} from "@/integrations/wordpress/cartUtils";
import { decodeHtmlEntities } from "@/lib/utils";

declare global {
  interface Window {
    wvcClient?: any;
  }
  const wvcClient: any;
}

function getWvcClient(): any {
  if (typeof wvcClient !== "undefined" && wvcClient != null) {
    return wvcClient;
  }
  if (typeof window !== "undefined" && window.wvcClient != null) {
    return window.wvcClient;
  }
  return undefined;
}

/* -------------------------------------------------------------------------- */
/* TYPES                                                                      */
/* -------------------------------------------------------------------------- */

export interface CartCoupon {
  code: string;
  discountFormatted: string;
  discountMinor: number;
  discountType?: string;
}

export interface CartFee {
  id: string;
  name: string;
  amountMinor: number;
  formatted: string;
  isNegative: boolean;
}

export interface CartShippingInfo {
  known: boolean;
  selectedRateName?: string;
  totalFormatted?: string;
}

export interface CartTaxInfo {
  known: boolean;
  totalFormatted?: string;
}

export interface CartItemQuantityLimits {
  min: number;
  max?: number;
  multipleOf: number;
  editable: boolean;
}

export interface FullCartItem extends CartItemView {
  regularUnitPrice?: number;
  regularUnitFormatted?: string;
  hasDiscountedUnitPrice: boolean;
  quantityLimits: CartItemQuantityLimits;
  strengthLabel?: string;
  rawVariationLabels: { attribute: string; value: string }[];
}

export interface FullCartTotalsView extends CartTotalsView {
  totalItemsMinor?: number;
  totalDiscountMinor?: number;
}

export interface FullCartView extends CartView {
  totals: FullCartTotalsView;
  items: FullCartItem[];
  coupons: CartCoupon[];
  fees: CartFee[];
  negativeFees: CartFee[];
  shipping: CartShippingInfo;
  tax: CartTaxInfo;
}

/* -------------------------------------------------------------------------- */
/* HELPERS                                                                    */
/* -------------------------------------------------------------------------- */

let cachedNonce: string | null = null;

function stripHtml(input: string): string {
  if (!input) return "";
  return decodeHtmlEntities(input.replace(/<[^>]*>?/gm, "").trim());
}

function coercePayload(raw: unknown): any {
  if (!raw || typeof raw !== "object") return raw;
  const payload = raw as Record<string, unknown>;
  if (
    payload.data != null &&
    typeof payload.data === "object" &&
    payload.items_count == null &&
    payload.totals == null &&
    !Array.isArray(raw)
  ) {
    return coercePayload(payload.data);
  }
  return payload;
}

function getStoreApiBaseUrl(): string {
  const client = getWvcClient();
  if (typeof client?.homeUrl === "function") {
    try {
      const url = String(client.homeUrl());
      if (url && url.startsWith("http")) {
        return url.replace(/\/+$/, "");
      }
    } catch {
      // fallback
    }
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, "");
  }
  return "";
}

/**
 * Extracts and updates cached Store API nonce from response headers.
 */
function recordNonceFromHeaders(headers: Headers): void {
  const nonce =
    headers.get("Nonce") ||
    headers.get("X-WC-Store-API-Nonce") ||
    headers.get("nonce") ||
    headers.get("x-wc-store-api-nonce");
  if (nonce) {
    cachedNonce = nonce;
  }
}

/**
 * Parses raw Store API payload into typed FullCartView.
 */
export function enrichRawCart(rawPayload: any): FullCartView {
  const coerced = coercePayload(rawPayload) || {};
  const baseCart = normalizeCart(coerced);
  const currency: CartCurrency = baseCart.totals.currency;

  // 1. Coupons
  const rawCoupons = Array.isArray(coerced.coupons) ? coerced.coupons : [];
  const coupons: CartCoupon[] = rawCoupons
    .map((c: any) => {
      const code = String(c?.code ?? "").trim();
      if (!code) return null;
      const totalsSource = c?.totals ?? {};
      const discountMinor = Number(
        totalsSource?.total_discount ?? c?.discount ?? 0
      );
      const discountType = c?.discount_type != null ? String(c.discount_type) : undefined;
      return {
        code,
        discountMinor,
        discountFormatted: formatCartMoney(discountMinor, currency),
        discountType,
      };
    })
    .filter((c): c is CartCoupon => c != null);

  // 2. Fees (Store API returns array of fee lines; negative fees represent discounts like quantity savings)
  const rawFees = Array.isArray(coerced.fees) ? coerced.fees : [];
  const fees: CartFee[] = rawFees
    .map((f: any, idx: number) => {
      const id = String(f?.id ?? f?.key ?? `fee-${idx}`);
      const name = stripHtml(String(f?.name ?? "Fee"));
      const amountMinor = Number(
        f?.totals?.total ?? f?.amount ?? f?.total ?? 0
      );
      const isNegative = amountMinor < 0;
      const absAmount = Math.abs(amountMinor);
      const formatted = `${isNegative ? "-" : ""}${formatCartMoney(absAmount, currency)}`;
      return {
        id,
        name,
        amountMinor,
        formatted,
        isNegative,
      };
    })
    .filter((f): f is CartFee => Boolean(f.name));

  const negativeFees = fees.filter((f) => f.isNegative);

  // 3. Shipping
  const rawShippingRates = Array.isArray(coerced.shipping_rates)
    ? coerced.shipping_rates
    : [];
  let selectedRateName: string | undefined;
  for (const group of rawShippingRates) {
    const rates = Array.isArray(group?.shipping_rates)
      ? group.shipping_rates
      : [];
    const selected = rates.find((r: any) => Boolean(r?.selected));
    if (selected?.name) {
      selectedRateName = stripHtml(String(selected.name));
      break;
    }
  }

  const shippingTotalMinor = Number(coerced.totals?.total_shipping ?? 0);
  const hasShippingAddress = Boolean(
    coerced.shipping_address?.country ||
      coerced.shipping_address?.city ||
      coerced.shipping_address?.postcode
  );
  const shippingKnown =
    Boolean(selectedRateName) ||
    (hasShippingAddress && shippingTotalMinor > 0);

  const shipping: CartShippingInfo = {
    known: shippingKnown,
    selectedRateName,
    totalFormatted: shippingKnown
      ? formatCartMoney(shippingTotalMinor, currency)
      : undefined,
  };

  // 4. Tax
  const taxTotalMinor = Number(coerced.totals?.total_tax ?? 0);
  const taxKnown =
    taxTotalMinor > 0 || (hasShippingAddress && coerced.totals?.total_tax != null);
  const tax: CartTaxInfo = {
    known: taxKnown,
    totalFormatted: taxKnown
      ? formatCartMoney(taxTotalMinor, currency)
      : undefined,
  };

  // 5. Items enrichment
  const rawItemsList = Array.isArray(coerced.items) ? coerced.items : [];
  const rawItemsByKey = new Map<string, any>();
  rawItemsList.forEach((rawItem: any) => {
    const key = String(rawItem?.key ?? rawItem?.id ?? "").trim();
    if (key) {
      rawItemsByKey.set(key, rawItem);
    }
  });

  const enrichedItems: FullCartItem[] = baseCart.items.map((baseItem) => {
    const raw = rawItemsByKey.get(baseItem.key) || {};
    const prices = raw.prices ?? {};
    const regularMinor = Number(
      prices.regular_price ?? prices.price ?? 0
    );
    const currentMinor = Number(
      prices.price ?? prices.sale_price ?? regularMinor
    );

    const hasDiscountedUnitPrice = regularMinor > currentMinor;
    const regularUnitPrice = minorToMajor(regularMinor, currency);
    const regularUnitFormatted = formatCartMoney(regularMinor, currency);

    // Limits
    const limits = raw.quantity_limits ?? {};
    const minQty = Math.max(1, Number(limits.minimum ?? 1));
    const maxQtyRaw = limits.maximum != null ? Number(limits.maximum) : undefined;
    const maxQty =
      typeof maxQtyRaw === "number" && !Number.isNaN(maxQtyRaw) && maxQtyRaw > 0
        ? maxQtyRaw
        : undefined;
    const multipleOf = Math.max(1, Number(limits.multiple_of ?? 1));
    const editable = Boolean(limits.editable ?? true);

    // Variation labels and strength extraction
    const rawVariationArray = Array.isArray(raw.variation) ? raw.variation : [];
    const rawVariationLabels: { attribute: string; value: string }[] =
      rawVariationArray
        .map((v: any) => ({
          attribute: stripHtml(String(v?.attribute ?? v?.name ?? "Option")),
          value: stripHtml(String(v?.value ?? "")),
        }))
        .filter((v: { attribute: string; value: string }) => v.value.length > 0);

    // Fall back to baseItem.variations if raw variation was empty
    const variationsToInspect =
      rawVariationLabels.length > 0 ? rawVariationLabels : baseItem.variations;

    // Detect strength label if present
    const strengthVar = variationsToInspect.find(
      (v) =>
        /strength|size|dose|potency/i.test(v.attribute) ||
        /\b\d+(?:\.\d+)?\s*mg\b/i.test(v.value)
    );
    const strengthLabel = strengthVar
      ? strengthVar.value
      : undefined;

    return {
      ...baseItem,
      regularUnitPrice,
      regularUnitFormatted,
      hasDiscountedUnitPrice,
      quantityLimits: {
        min: minQty,
        max: maxQty,
        multipleOf,
        editable,
      },
      strengthLabel,
      rawVariationLabels: variationsToInspect,
    };
  });

  const totalItemsMinor = Number(coerced.totals?.total_items ?? 0);
  const totalDiscountMinor = Number(coerced.totals?.total_discount ?? 0);

  return {
    ...baseCart,
    totals: {
      ...baseCart.totals,
      totalItemsMinor,
      totalDiscountMinor,
    },
    items: enrichedItems,
    coupons,
    fees,
    negativeFees,
    shipping,
    tax,
  };
}

/* -------------------------------------------------------------------------- */
/* READ CART                                                                  */
/* -------------------------------------------------------------------------- */

export async function fetchFullCart(): Promise<FullCartView> {
  const client = getWvcClient();
  let clientErr: any = null;

  if (typeof client?.getCart === "function") {
    try {
      const raw = await client.getCart(true);
      if (raw && typeof raw === "object") {
        return enrichRawCart(raw);
      }
    } catch (err) {
      clientErr = err;
      console.warn("[cartApi] wvcClient.getCart failed, falling back to REST", err);
    }
  }

  // Fallback to direct Store API GET
  const base = getStoreApiBaseUrl();
  if (!base) {
    if (clientErr) {
      throw new Error(stripHtml(clientErr?.message || "Unable to load cart."));
    }
    return enrichRawCart({});
  }

  try {
    const res = await fetch(`${base}/wp-json/wc/store/v1/cart`, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(cachedNonce ? { Nonce: cachedNonce } : {}),
      },
    });

    recordNonceFromHeaders(res.headers);

    if (!res.ok) {
      let errorMsg = `Unable to load cart (status ${res.status}).`;
      try {
        const errJson = await res.json();
        if (errJson?.message) {
          errorMsg = stripHtml(String(errJson.message));
        }
      } catch {
        // ignore parse error
      }
      throw new Error(errorMsg);
    }

    const json = await res.json();
    return enrichRawCart(json);
  } catch (err: any) {
    console.error("[cartApi] fetchFullCart error:", err);
    throw new Error(stripHtml(err?.message || "Unable to load your cart. Please try again."));
  }
}

/* -------------------------------------------------------------------------- */
/* MUTATIONS (Store API / wvcClient)                                          */
/* -------------------------------------------------------------------------- */

async function fetchStoreApiNonce(): Promise<string | null> {
  const base = getStoreApiBaseUrl();
  if (!base) return null;
  try {
    const res = await fetch(`${base}/wp-json/wc/store/v1/cart`, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    recordNonceFromHeaders(res.headers);
    return cachedNonce;
  } catch {
    return null;
  }
}

interface MutationOptions {
  endpoint: string;
  body: Record<string, unknown>;
  retryOnNonce?: boolean;
}

async function executeStoreApiMutation(
  opts: MutationOptions
): Promise<FullCartView> {
  const { endpoint, body, retryOnNonce = true } = opts;
  const base = getStoreApiBaseUrl();
  if (!base) {
    throw new Error("Store endpoint is not available.");
  }

  const url = `${base}/wp-json/wc/store/v1/cart/${endpoint.replace(/^\/+/, "")}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  if (cachedNonce) {
    headers["Nonce"] = cachedNonce;
    headers["X-WC-Store-API-Nonce"] = cachedNonce;
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify(body),
    });
  } catch (err: any) {
    throw new Error(err?.message || "Network request failed.");
  }

  recordNonceFromHeaders(res.headers);

  // Check for nonce failure / 403 or 401: obtain a fresh nonce and retry with it explicitly
  if ((res.status === 403 || res.status === 401) && retryOnNonce) {
    const freshNonce = await fetchStoreApiNonce();
    if (freshNonce) {
      cachedNonce = freshNonce;
      return executeStoreApiMutation({
        endpoint,
        body,
        retryOnNonce: false,
      });
    }
  }

  let payload: any = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const rawMsg =
      payload?.message ||
      payload?.error ||
      `Store request failed with status ${res.status}`;
    throw new Error(stripHtml(String(rawMsg)));
  }

  // The Store API mutation response is the fresh full cart
  if (payload && typeof payload === "object") {
    return enrichRawCart(payload);
  }

  // If response didn't contain full cart, re-read
  return fetchFullCart();
}

/**
 * Updates item quantity in the cart.
 */
export async function updateItemQuantity(
  key: string,
  quantity: number
): Promise<FullCartView> {
  const client = getWvcClient();

  // Defensive check for helper methods on wvcClient.cart
  if (typeof client?.cart?.updateItem === "function") {
    try {
      const res = await client.cart.updateItem({ key, quantity });
      if (res && typeof res === "object") {
        return enrichRawCart(res);
      }
      return fetchFullCart();
    } catch (err: any) {
      throw new Error(stripHtml(err?.message || "Failed to update item quantity."));
    }
  }

  return executeStoreApiMutation({
    endpoint: "update-item",
    body: { key, quantity },
  });
}

/**
 * Removes an item from the cart.
 */
export async function removeCartItem(key: string): Promise<FullCartView> {
  const client = getWvcClient();

  if (typeof client?.cart?.removeItem === "function") {
    try {
      const res = await client.cart.removeItem({ key });
      if (res && typeof res === "object") {
        return enrichRawCart(res);
      }
      return fetchFullCart();
    } catch (err: any) {
      throw new Error(stripHtml(err?.message || "Failed to remove item."));
    }
  }

  return executeStoreApiMutation({
    endpoint: "remove-item",
    body: { key },
  });
}

/**
 * Applies a coupon code to the cart.
 */
export async function applyCoupon(code: string): Promise<FullCartView> {
  const cleanCode = code.trim();
  if (!cleanCode) {
    throw new Error("Please enter a promo code.");
  }

  const client = getWvcClient();
  if (typeof client?.cart?.applyCoupon === "function") {
    try {
      const res = await client.cart.applyCoupon({ code: cleanCode });
      if (res && typeof res === "object") {
        return enrichRawCart(res);
      }
      return fetchFullCart();
    } catch (err: any) {
      throw new Error(stripHtml(err?.message || "Invalid promo code."));
    }
  }

  return executeStoreApiMutation({
    endpoint: "apply-coupon",
    body: { code: cleanCode },
  });
}

/**
 * Removes an applied coupon code from the cart.
 */
export async function removeCoupon(code: string): Promise<FullCartView> {
  const cleanCode = code.trim();
  if (!cleanCode) return fetchFullCart();

  const client = getWvcClient();
  if (typeof client?.cart?.removeCoupon === "function") {
    try {
      const res = await client.cart.removeCoupon({ code: cleanCode });
      if (res && typeof res === "object") {
        return enrichRawCart(res);
      }
      return fetchFullCart();
    } catch (err: any) {
      throw new Error(stripHtml(err?.message || "Failed to remove promo code."));
    }
  }

  return executeStoreApiMutation({
    endpoint: "remove-coupon",
    body: { code: cleanCode },
  });
}
